import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import CustomMUITable from "src/components/CustomMUITable";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import Label from "src/components/Label";
import {
  deleteAutomationViaApi,
  loadAutomationsFromApi,
} from "./automations/automationStorage";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

const StatCard = ({ label, value, hint }) => (
  <Box
    sx={{
      flex: "1 1 120px",
      minWidth: 110,
      p: 1.5,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.4, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ mt: 0.35, fontSize: 11, color: MUTED }}>{hint}</Typography>
    ) : null}
  </Box>
);

/**
 * Automations for one tracker — multiple parents allowed.
 */
export default function TrackerDetailAutomationsTab({ tracker }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tracker?.id) {
        if (!cancelled) {
          setParents([]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      const res = await loadAutomationsFromApi(tracker.id);
      if (!cancelled) {
        setParents(res.automations || []);
        setLoading(false);
        if (!res.ok) {
          enqueueSnackbar(res.message || "Failed to load automations", {
            variant: "error",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tracker?.id, enqueueSnackbar]);

  const load = async () => {
    if (!tracker?.id) {
      setParents([]);
      return;
    }
    const res = await loadAutomationsFromApi(tracker.id);
    setParents(res.automations || []);
    if (!res.ok) {
      enqueueSnackbar(res.message || "Failed to load automations", {
        variant: "error",
      });
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parents;
    return parents.filter((row) => {
      const hay = `${row.name || ""} ${row.description || ""} ${
        row.status || ""
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [parents, search]);

  const stats = useMemo(() => {
    const live = parents.filter((r) => r.enabled !== false).length;
    const workflows = parents.reduce(
      (sum, row) => sum + (row.workflows?.length || 0),
      0
    );
    return {
      total: parents.length,
      live,
      off: parents.length - live,
      workflows,
    };
  }, [parents]);

  const createAutomation = () => {
    navigate(
      `/tracker-automation-builder/new?trackerId=${encodeURIComponent(
        tracker.id
      )}`
    );
  };

  const openAutomation = (row) => {
    if (!row?.id) return;
    navigate(`/tracker-automation-builder/${row.id}`);
  };

  const handleDelete = async () => {
    if (!deleteDoc?.id) return;
    try {
      await deleteAutomationViaApi(deleteDoc.id);
      setOpenDelete(false);
      setDeleteDoc(null);
      await load();
      enqueueSnackbar("Automation deleted", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not delete automation", {
        variant: "error",
      });
    }
  };

  const MENU_OPTIONS = [
    {
      label: "Open",
      icon: "akar-icons:eye",
      handleClick: (row) => openAutomation(row),
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: (row) => {
        setDeleteDoc(row);
        setOpenDelete(true);
      },
    },
  ];

  const TABLE_HEAD = [
    {
      id: "action",
      label: "Action",
      type: "action",
      MENU_OPTIONS: "MENU_OPTIONS",
    },
    { id: "number", label: "#", type: "number" },
    { id: "name", label: "Automation" },
    { id: "workflows", label: "Workflows" },
    { id: "status", label: "Status" },
    { id: "updated", label: "Updated" },
  ];

  const tableRows = filtered.map((row) => ({
    ...row,
    name: (
      <Box>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 600,
            color: PRIMARY,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => openAutomation(row)}
        >
          {row.name || "Untitled automation"}
        </Typography>
        {row.description ? (
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            {row.description}
          </Typography>
        ) : null}
      </Box>
    ),
    workflows: (
      <Typography sx={{ fontSize: 13 }}>
        {(row.workflows || []).length}
      </Typography>
    ),
    status: (
      <Label
        variant="ghost"
        color={row.enabled !== false ? "success" : "default"}
      >
        {row.enabled !== false ? "On" : "Off"}
      </Label>
    ),
    updated: (
      <Typography sx={{ fontSize: 13 }}>
        {row.updatedAt
          ? moment(row.updatedAt).format("DD MMM YYYY")
          : row.createdAt
            ? moment(row.createdAt).format("DD MMM YYYY")
            : "—"}
      </Typography>
    ),
    MENU_OPTIONS,
  }));

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Delete this automation and all its workflows?"
        handleAgree={handleDelete}
      />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            Automations
          </Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            Add as many as you need — each holds its own workflows.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <button
            type="button"
            className="small-contained-button"
            onClick={createAutomation}
          >
            Create automation
          </button>
        </Stack>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
        <StatCard
          label="Automations"
          value={stats.total}
          hint="On this tracker"
        />
        <StatCard label="On" value={stats.live} hint="Enabled" />
        <StatCard label="Off" value={stats.off} hint="Disabled" />
        <StatCard
          label="Workflows"
          value={stats.workflows}
          hint="Across all"
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: PRIMARY }} />
        </Box>
      ) : !parents.length ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: "12px",
            border: `1px dashed ${BORDER}`,
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
            No automation for this tracker yet
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 13, color: MUTED, mb: 2 }}>
            Create one, then add workflows for reminders, coach reviews, or
            follow-ups.
          </Typography>
          <button
            type="button"
            className="small-contained-button"
            onClick={createAutomation}
          >
            Create automation
          </button>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              mb: 1.5,
            }}
          >
            <TextField
              size="small"
              placeholder="Search automations…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, width: { xs: "100%", sm: 240 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {filtered.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: "12px",
                border: `1px dashed ${BORDER}`,
                bgcolor: "#fff",
              }}
            >
              <Typography sx={{ fontSize: 13.5, color: MUTED }}>
                No automations match your search.
              </Typography>
            </Box>
          ) : (
            <CustomMUITable
              TABLE_HEAD={TABLE_HEAD}
              data={tableRows}
              MENU_OPTIONS={MENU_OPTIONS}
              hide_search
              className="card-with-background tracker-module-table"
            />
          )}
        </>
      )}
    </>
  );
}
